/**
 * Section « Produits liés » d'une CGU « produits ».
 *
 * Comportement :
 * - Select2 ajax sur la recherche de produits actifs (par référence ou désignation).
 * - Auto-attach au choix d'un résultat → POST → re-render serveur du tableau + toast.
 * - Suppression à la ligne + « Tout supprimer » via swal → DELETE/POST → re-render + toast.
 *
 * Le composant lit ses URL/CSRF/traductions depuis les `data-*` du wrapper.
 * Voir `resources/views/tabler/backoffice/cgu/includes/products.blade.php`.
 */
export default class CguProductsList extends HTMLDivElement {
    constructor() {
        super();

        this.csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        this.searchUrl = this.dataset.searchUrl;
        this.attachUrl = this.dataset.attachUrl;
        this.cguId = this.dataset.cguId;
        this.listSelector = '#cgu-products-list';
        this.selectSelector = '#cgu-product-search';
        this.translations = {
            placeholder: this.dataset.placeholder,
            detachProductTitle: this.dataset.detachProductTitle,
            detachProductTextTemplate: this.dataset.detachProductTextTemplate,
            detachAllTitle: this.dataset.detachAllTitle,
            detachAllText: this.dataset.detachAllText,
            confirmDelete: this.dataset.confirmDelete,
            cancel: this.dataset.cancel,
            errorMessage: this.dataset.errorMessage,
        };
    }

    connectedCallback() {
        this.initSelect2();
        this.bindDetachButtons();
        this.bindDetachAllButton();
    }

    initSelect2() {
        const $select = $(this.selectSelector);
        if ( ! $select.length || typeof $select.select2 !== 'function') {
            return;
        }

        $select.select2({
            placeholder: this.translations.placeholder,
            allowClear: false,
            minimumInputLength: 2,
            ajax: {
                url: this.searchUrl,
                dataType: 'json',
                delay: 250,
                data: (params) => ({
                    query: params.term,
                    cgu_id: this.cguId,
                }),
                processResults: (data) => ({
                    results: (data.products || []).map((p) => ({
                        id: p.id,
                        text: `${p.reference} — ${p.name}`,
                    })),
                }),
            },
        });

        $select.on('select2:select', (e) => this.handleAttach(e.params.data.id, $select));
    }

    handleAttach(productId, $select) {
        $.ajax({
            method: 'POST',
            url: this.attachUrl,
            data: { _token: this.csrfToken, product_id: productId },
            dataType: 'json',
        }).done((data) => {
            this.refreshList(data.view);
            this.notifySuccess(data.message);
            $select.val(null).trigger('change');
        }).fail(() => {
            this.notifyError(this.translations.errorMessage);
        });
    }

    bindDetachAllButton() {
        const btn = this.querySelector('#cgu-detach-all-btn');
        if ( ! btn) {
            return;
        }

        btn.addEventListener('click', () => {
            const form = this.querySelector('#cgu-detach-all-form');

            swal({
                title: this.translations.detachAllTitle,
                text: this.translations.detachAllText,
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#D2282D',
                confirmButtonText: this.translations.confirmDelete,
                cancelButtonText: this.translations.cancel,
                showLoaderOnConfirm: true,
                preConfirm: () => $.ajax({
                    method: 'POST',
                    url: form.getAttribute('action'),
                    data: { _token: this.csrfToken },
                    dataType: 'json',
                }),
            }).then((result) => {
                if (result.value) {
                    this.refreshList(result.value.view);
                    this.notifySuccess(result.value.message);
                }
            });
        });
    }

    bindDetachButtons() {
        this.querySelectorAll('.cgu-detach-product').forEach((btn) => {
            btn.addEventListener('click', () => {
                const url = btn.getAttribute('data-detach-url');
                const label = btn.getAttribute('data-product-label');
                const text = this.translations.detachProductTextTemplate.replace('__LABEL__', label);

                swal({
                    title: this.translations.detachProductTitle,
                    text: text,
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#D2282D',
                    confirmButtonText: this.translations.confirmDelete,
                    cancelButtonText: this.translations.cancel,
                    showLoaderOnConfirm: true,
                    preConfirm: () => $.ajax({
                        method: 'DELETE',
                        url: url,
                        data: { _token: this.csrfToken },
                        dataType: 'json',
                    }),
                }).then((result) => {
                    if (result.value) {
                        this.refreshList(result.value.view);
                        this.notifySuccess(result.value.message);
                    }
                });
            });
        });
    }

    refreshList(html) {
        const wrapper = this.querySelector(this.listSelector);
        if ( ! wrapper) {
            return;
        }
        wrapper.innerHTML = html;
        // Rebinder les listeners sur les nouveaux DOM nodes.
        this.bindDetachButtons();
        this.bindDetachAllButton();
    }

    notifySuccess(message) {
        if (message && typeof CoreNotification !== 'undefined' && CoreNotification.success) {
            CoreNotification.success(message);
        }
    }

    notifyError(message) {
        if (message && typeof CoreNotification !== 'undefined' && CoreNotification.error) {
            CoreNotification.error(message);
        }
    }
}
